import { ActionIcon, ColorSwatch, Container, Flex, Group, Menu, Stack, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import {
  IconDots,
  IconEdit,
  IconList,
  IconMail,
  IconPackageImport,
  IconPlus,
  IconRainbow,
  IconRainbowOff,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useDeleteShortageOrder, useShortageOrders } from 'hooks/shortage'
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  useMantineReactTable,
} from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Account } from 'types/auth'
import { Category, Manufacturer } from 'types/products'
import { ShortageOrderTableItem, ShortageStatus } from 'types/shortage'
import {
  categoriesForSelect,
  getCategoryName,
  getShortageOrderStatus,
  getShortageOrderStatusText,
  getUnitName,
  shortageOrderStatusOptions,
} from 'utils'
import ShortageOrderInfo from './ShortageOrderInfo'

type Props = {
  users: Array<Account>
  manufacturers: Array<Manufacturer>
}

export default function ShortageOrderTable({ users, manufacturers }: Props) {
  const [isColoredRowsEnabled, setIsColoredRowsEnabled] = useState<boolean>(true)
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const deleteShortageOrder = useDeleteShortageOrder()

  function openUpdateShortageOrderModal(shortageOrder: ShortageOrderTableItem) {
    modals.openContextModal({
      modal: 'updateShortageOrder',
      centered: true,
      title: 'Edytuj zamówienie braków',
      innerProps: {
        shortageOrder,
      },
    })
  }

  function openDeleteShortageOrderModal(shortageOrder: ShortageOrderTableItem) {
    modals.openConfirmModal({
      title: 'Usuń zamówienie braków produktów',
      centered: true,
      children: (
        <Stack gap="md">
          <Text size="sm">
            Czy na pewno chcesz usunąć zamówienie tego produktu? Nie będzie można cofnąć tej operacji. Wszystkie braki
            przypisane do tego zamówienia zostaną oznaczone jako niezamówione.
          </Text>
          <ShortageOrderInfo shortageOrder={shortageOrder} />
        </Stack>
      ),
      labels: { confirm: 'Usuń zamówienie', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteShortageOrder.mutateAsync(shortageOrder.id),
    })
  }

  function openSendShortageOrderEmailModal(selectedRows: Array<ShortageOrderTableItem>) {
    modals.openContextModal({
      modal: 'sendShortageOrderEmail',
      centered: true,
      title: 'Wyślij email',
      size: 'lg',
      onClose: () => {
        setRowSelection({})
      },
      innerProps: {
        selectedRows,
      },
    })
  }

  function openShortagesModal(shortageOrder: ShortageOrderTableItem) {
    modals.openContextModal({
      modal: 'shortages',
      centered: true,
      title: 'Braki produktu dla zamówienia',
      size: 'lg',
      innerProps: {
        shortageOrder: shortageOrder,
      },
    })
  }

  function openFulfillShortageOrderModal(shortageOrder: ShortageOrderTableItem) {
    modals.openContextModal({
      modal: 'fulfillShortageOrder',
      centered: true,
      title: 'Zrealizuj zamówienie braków produktu',
      innerProps: {
        shortageOrder,
      },
    })
  }

  function openNewShortageOrderModal() {
    modals.openContextModal({
      modal: 'addShortageOrder',
      centered: true,
      title: 'Nowe zamówienie',
      innerProps: {},
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<ShortageOrderTableItem>>>(
    () => [
      {
        accessorKey: 'product.symbol',
        header: 'Symbol produktu',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po symbolu',
        },
        size: 210,
        Cell: ({ cell }) => {
          const productSymbol = cell.getValue<string>()
          const fulfilledAt = cell.row.original.fulfilledAt
          const createdAt = cell.row.original.createdAt
          if (fulfilledAt) return productSymbol
          const daysSinceCreated = dayjs().diff(dayjs(createdAt), 'day')
          if (daysSinceCreated < 5) return productSymbol

          return (
            <Stack gap={0}>
              <span>{productSymbol}</span>
              <Text fw={600} c="red.8" size="xs">
                Zamówione {daysSinceCreated} dni temu
              </Text>
            </Stack>
          )
        },
      },
      {
        accessorKey: 'size',
        header: 'Wymiar',
        size: 160,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po wymiarze',
        },
      },
      {
        accessorKey: 'category',
        header: 'Rodzaj produktu',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po rodzaju produktu',
          data: categoriesForSelect,
        },
        Cell: ({ cell }) => {
          const category = cell.getValue<Category>()
          const categoryName = getCategoryName(category, { capitalize: true }).mianownik
          return categoryName
        },
      },
      {
        accessorKey: 'feature',
        header: 'Cecha',
        size: 150,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po cesze',
        },
      },
      {
        accessorKey: 'shortageAmount',
        header: 'Brakująca ilość',
        size: 140,
        enableColumnFilter: false,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po brakującej ilości',
        },
        Cell: ({ cell }) => {
          const shortageAmount = cell.getValue<number>()
          const unit = cell.row.original.unit
          if (shortageAmount === 0 && getShortageOrderStatus(cell.row.original) === ShortageStatus.UNFULFILLED) {
            return ``
          }
          return `${formatNumber(shortageAmount)} ${getUnitName(unit).short}`
        },
      },
      {
        accessorKey: 'orderedAmount',
        header: 'Zamówiona ilość',
        size: 150,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po zamówionej ilości',
        },
        Cell: ({ cell }) => {
          const orderedAmount = cell.getValue<number>()
          const unit = cell.row.original.unit
          // display number always with at least one decimal place
          return `${formatNumber(orderedAmount)} ${getUnitName(unit).short}`
        },
      },
      {
        accessorKey: 'fulfilledAmount',
        header: 'Zrealizowana ilość',
        size: 161,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po zrealizowanej ilości',
        },
        Cell: ({ cell }) => {
          const fulfilledAmount = cell.getValue<number>()
          const unit = cell.row.original.unit
          if (!fulfilledAmount) return ''
          if (fulfilledAmount === -1) return ''
          return `${formatNumber(fulfilledAmount)} ${getUnitName(unit).short}`
        },
      },
      {
        accessorKey: 'manufacturer.name',
        header: 'Producent',
        size: 150,
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po producencie',
          data: manufacturers.map((manufacturer) => manufacturer.name),
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Data zamówienia',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date>()
          if (!createdAt) return ''
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie dodania',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
        size: 200,
      },
      {
        accessorKey: 'fulfilledAt',
        header: 'Data realizacji',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const fulfilledAt = cell.getValue<Date>()
          return fulfilledAt ? dayjs(fulfilledAt).format('D MMMM HH:mm') : ''
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie realizacji',
          locale: 'pl',
        },
        size: 200,
      },
      {
        accessorFn: (row) => row,
        header: 'Status',
        id: 'status',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po statusie',
          data: shortageOrderStatusOptions,
        },
        Cell: ({ cell }) => {
          return <Text size="sm">{getShortageOrderStatusText(cell.getValue<ShortageOrderTableItem>())}</Text>
        },
        size: 200,
      },
      {
        accessorKey: 'createdById',
        header: 'Dodane przez',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po użytkowniku',
          data: users.map((user) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          })),
        },
        Cell: ({ cell }) => {
          const createdById = cell.getValue<string>()
          if (!createdById) return ''
          const user = users.find((user) => user.id === createdById)
          return `${user?.firstName} ${user?.lastName}`
        },
        size: 210,
      },
    ],
    []
  )

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const { data, isLoading, isError, error, isFetching, refetch } = useShortageOrders({
    columnFilters,
    pagination,
  })

  const memoizedData = useMemo(() => data ?? { data: [], total: 0 }, [JSON.stringify(data)])

  const selectedRowDataRef = useRef<Record<string, ShortageOrderTableItem>>({})
  const [allSelectedRows, setAllSelectedRows] = useState<Array<ShortageOrderTableItem>>([])

  useEffect(() => {
    const currentData = memoizedData.data
    const nextSelectedData: Record<string, ShortageOrderTableItem> = {}
    for (const id of Object.keys(rowSelection)) {
      if (rowSelection[id]) {
        const rowFromCurrentPage = currentData.find((row) => row.id === id)
        nextSelectedData[id] = rowFromCurrentPage ?? selectedRowDataRef.current[id]
      }
    }
    selectedRowDataRef.current = nextSelectedData
    setAllSelectedRows(Object.values(nextSelectedData).filter(Boolean))
  }, [rowSelection, memoizedData.data])

  const table = useMantineReactTable({
    columns,
    data: memoizedData.data,
    layoutMode: 'grid',
    rowCount: memoizedData.total,
    state: {
      columnFilters,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showColumnFilters: true,
      rowSelection,
    },
    initialState: {
      showGlobalFilter: false,
      showColumnFilters: true,
      columnPinning: { right: ['mrt-row-actions'] },
    },
    getRowId: (row) => row.id,
    enableGlobalFilterModes: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableBottomToolbar: true,
    enableColumnPinning: true,
    manualFiltering: true,
    manualPagination: true,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    autoResetPageIndex: false,
    enableRowSelection: true,
    renderTopToolbarCustomActions: () => {
      return (
        <Container mr={0} pr={0}>
          {allSelectedRows.length > 0 && (
            <Tooltip label="Wyślij email z zamówieniem">
              <ActionIcon
                size="lg"
                variant="subtle"
                mr={4}
                onClick={() => openSendShortageOrderEmailModal(allSelectedRows)}
              >
                <IconMail />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Dodaj nowe zamówienie">
            <ActionIcon size="lg" variant="subtle" color="gray" onClick={openNewShortageOrderModal}>
              <IconPlus />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={isColoredRowsEnabled ? 'Wyłącz kolory' : 'Włącz kolory'}>
            <ActionIcon size="lg" variant="subtle" color="gray" onClick={() => setIsColoredRowsEnabled((v) => !v)}>
              {isColoredRowsEnabled ? <IconRainbowOff /> : <IconRainbow />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Odśwież dane">
            <ActionIcon size="lg" variant="subtle" color="gray" onClick={() => refetch()}>
              <IconRefresh />
            </ActionIcon>
          </Tooltip>
        </Container>
      )
    },
    mantineToolbarAlertBannerBadgeProps: {
      style: { position: 'absolute' },
    },
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error})`,
        }
      : undefined,
    mantineTableBodyRowProps: ({ row }) => ({
      style:
        isColoredRowsEnabled && memoizedData.total > 0
          ? {
              backgroundColor: getBackgroundColor(row.original, row.getIsSelected(), false),
              '--mrt-pinned-column-background-color': getBackgroundColor(row.original, row.getIsSelected(), false),
              '--mrt-row-hover-background-color': getBackgroundColor(row.original, row.getIsSelected(), true),
              '--mrt-striped-row-hover-background-color': getBackgroundColor(row.original, row.getIsSelected(), true),
              '--mrt-selected-row-background-color': getBackgroundColor(row.original, true, false),
              '--mrt-selected-row-hover-background-color': getBackgroundColor(row.original, true, true),
            }
          : undefined,
    }),
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10', '15'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
        enablePinning: true,
      },
    },
    renderBottomToolbarCustomActions: () => {
      return (
        <Group>
          <Stack>
            <Group gap="xs">
              <ColorSwatch color="var(--mantine-color-yellow-1)" size={20} />
              <Text size="sm">Stworzone</Text>
            </Group>
            <Group gap="xs">
              <ColorSwatch color="var(--mantine-color-green-1)" size={20} />
              <Text size="sm">Zrealizowane</Text>
            </Group>
          </Stack>
          <Stack>
            <Group gap="xs">
              <ColorSwatch color="var(--mantine-color-indigo-1)" size={20} />
              <Text size="sm">Zamówione u producenta</Text>
            </Group>

            <Group gap="xs">
              <ColorSwatch color="var(--mantine-color-red-2)" size={20} />
              <Text size="sm">Niezrealizowane</Text>
            </Group>
          </Stack>
        </Group>
      )
    },
    renderRowActions: ({ row }) => {
      const shortageOrder = row.original
      return (
        <Flex gap="sm" justify="end">
          <Tooltip label={shortageOrder.fulfilledAt ? 'Zamówienie jest już zrealizowane' : 'Zrealizuj'}>
            <ActionIcon
              variant="light"
              radius="xl"
              onClick={() => openFulfillShortageOrderModal(shortageOrder)}
              disabled={!!shortageOrder.fulfilledAt}
            >
              <IconPackageImport size={18} />
            </ActionIcon>
          </Tooltip>
          <Menu withArrow arrowSize={10} position="bottom-end" withinPortal offset={0} arrowOffset={8}>
            <Menu.Target>
              <ActionIcon radius="xl" variant="subtle" color="gray">
                <IconDots size={18} stroke={2} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconList size={16} stroke={1.5} />}
                onClick={() => openShortagesModal(shortageOrder)}
              >
                Braki
              </Menu.Item>
              <Menu.Item
                leftSection={<IconEdit size={16} stroke={1.5} />}
                onClick={() => openUpdateShortageOrderModal(shortageOrder)}
              >
                Edytuj
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={16} stroke={1.5} />}
                color="red"
                onClick={() => openDeleteShortageOrderModal(shortageOrder)}
                disabled={!!shortageOrder.fulfilledAt}
              >
                Usuń
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      )
    },
  })

  return <MantineReactTable table={table} />
}

function formatNumber(num: number): string {
  const str = num.toString()
  return str.includes('.') ? str : `${str}.0`
}

function getBackgroundColor(shortageOrder: ShortageOrderTableItem, isSelected: boolean, isHovered: boolean): string {
  let color = 0
  if (isSelected) {
    color += 1
  }
  if (isHovered) {
    color += 1
  }
  const status = getShortageOrderStatus(shortageOrder)

  if (status === ShortageStatus.FULFILLED) {
    return `var(--mantine-color-green-${color})`
  } else if (status === ShortageStatus.UNFULFILLED) {
    color += 1
    return `var(--mantine-color-red-${color})`
  } else if (status === ShortageStatus.ORDERED) {
    return `var(--mantine-color-indigo-${color})`
  } else {
    return `var(--mantine-color-yellow-${color})`
  }
}
